// @ts-nocheck
/* eslint-disable */
import * as React from 'react';
const { Fragment, createContext, useContext, useEffect, useMemo, useRef, useState } = React;
const h = React.createElement;

// ========== Form store ==========
const formStoreMap = new WeakMap();
const FormStoreContext = createContext(null);

function createFormStore() {
  const values = {};
  const errors = {};
  const touched = {};
  const listeners = new Set();
  const rulesMap = {};

  function notify() {
    listeners.forEach((fn) => fn());
  }
  function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }
  function getFieldValue(name) {
    return values[name];
  }
  function getFieldsValue() {
    return Object.assign({}, values);
  }
  function setFieldsValue(newValues) {
    if (!newValues || typeof newValues !== 'object') return;
    for (const k of Object.keys(newValues)) {
      values[k] = newValues[k];
    }
    notify();
  }
  function setFieldRules(name, rules) {
    rulesMap[name] = rules;
  }

  async function validateFields(nameList) {
    const names = nameList || Object.keys(rulesMap);
    const allErrors = {};
    const promises = [];

    for (const name of names) {
      const rules = rulesMap[name] || [];
      const value = values[name];
      const fieldErrors = [];

      for (const rule of rules) {
        if (rule.required && (value === undefined || value === null || value === '')) {
          fieldErrors.push(rule.message || `${name} is required`);
        } else if (value !== undefined && value !== null && value !== '') {
          if (rule.min !== undefined && typeof value === 'string' && value.length < rule.min) {
            fieldErrors.push(rule.message || `Min ${rule.min} characters`);
          }
          if (rule.max !== undefined && typeof value === 'string' && value.length > rule.max) {
            fieldErrors.push(rule.message || `Max ${rule.max} characters`);
          }
          if (rule.pattern && !rule.pattern.test(String(value))) {
            fieldErrors.push(rule.message || 'Invalid format');
          }
          if (rule.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))) {
            fieldErrors.push(rule.message || 'Invalid email');
          }
          if (rule.validator) {
            promises.push(
              Promise.resolve()
                .then(() => rule.validator({}, value))
                .catch((e) => {
                  fieldErrors.push(e.message || 'Validation failed');
                }),
            );
          }
        }
      }

      if (fieldErrors.length > 0) allErrors[name] = fieldErrors;
    }

    await Promise.all(promises);

    for (const name of names) {
      const rules = rulesMap[name] || [];
      const value = values[name];
      for (const rule of rules) {
        if (rule.validator && value !== undefined && value !== null && value !== '') {
          try {
            await rule.validator({}, value);
          } catch (e) {
            if (!allErrors[name]) allErrors[name] = [];
            if (!allErrors[name].includes(e.message)) allErrors[name].push(e.message);
          }
        }
      }
    }

    // 同步错误状态：清空已通过的字段，覆盖未通过的字段
    for (const name of names) {
      errors[name] = allErrors[name] || [];
    }
    notify();

    if (Object.keys(allErrors).length > 0) {
      const err = new Error('Validation failed');
      err.errorFields = allErrors;
      throw err;
    }
    return getFieldsValue();
  }

  function resetFields(nameList) {
    // 若未指定字段，重置全部
    const names = Array.isArray(nameList) && nameList.length > 0 ? nameList : Object.keys(values);
    for (const k of names) {
      values[k] = undefined;
      if (errors[k]) errors[k] = [];
      if (touched[k]) touched[k] = false;
    }
    // 未指定时，确保 errors/touched 也完全清空（处理那些不存在于 values 的字段）
    if (!Array.isArray(nameList) || nameList.length === 0) {
      for (const k of Object.keys(errors)) errors[k] = [];
      for (const k of Object.keys(touched)) touched[k] = false;
    }
    notify();
  }

  function getFieldError(name) {
    return errors[name] || [];
  }

  function setFieldValue(name, value) {
    values[name] = value;
    // 用户交互写值时，清除该字段的旧错误
    if (errors[name] && errors[name].length > 0) {
      errors[name] = [];
    }
    notify();
  }

  return {
    values,
    errors,
    touched,
    rulesMap,
    subscribe,
    getFieldValue,
    getFieldsValue,
    setFieldsValue,
    setFieldRules,
    validateFields,
    resetFields,
    getFieldError,
    setFieldValue,
  };
}

function bindFormInstance(form, store) {
  form.__store = store;
  form.getFieldValue = (name) => store.getFieldValue(name);
  form.getFieldsValue = () => store.getFieldsValue();
  form.setFieldsValue = (vals) => store.setFieldsValue(vals);
  form.resetFields = (names) => store.resetFields(names);
  form.validateFields = (names) => store.validateFields(names);
  formStoreMap.set(form, store);
}

function useForm(form = undefined) {
  const formRef = useRef(form || undefined);
  if (!formRef.current) {
    const instance = {};
    const store = createFormStore();
    bindFormInstance(instance, store);
    formRef.current = instance;
  }
  return [formRef.current] as any;
}

// ========== Form ==========
const Form = function Form(props) {
  const {
    form,
    children,
    onFinish,
    onFinishFailed,
    layout = 'vertical',
    autoComplete = 'off',
    className = '',
    initialValues,
    ...rest
  } = props;

  // 1) 解析 / 创建 store：优先用外部传入 form 的 __store
  const storeRef = useRef(null);
  if (!storeRef.current) {
    if (form && form.__store) {
      storeRef.current = form.__store;
    } else {
      const store = createFormStore();
      if (form) bindFormInstance(form, store);
      storeRef.current = store;
    }
  }
  const store = storeRef.current;

  // 2) 外部 form 实例首次挂到 DOM 之前，确保其 API 指向当前 store
  //    （兼容调用方 useForm 先建 store、这里又创建的情况）
  if (form && form.__store !== store) {
    bindFormInstance(form, store);
  }

  // 3) initialValues 只应用一次（仅当 store 值为空）
  const initApplied = useRef(false);
  if (!initApplied.current && initialValues) {
    const current = store.getFieldsValue();
    const patch = {};
    for (const k of Object.keys(initialValues)) {
      if (current[k] === undefined) patch[k] = initialValues[k];
    }
    if (Object.keys(patch).length > 0) store.setFieldsValue(patch);
    initApplied.current = true;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const values = await store.validateFields();
      if (onFinish) onFinish(values);
    } catch (err) {
      if (onFinishFailed) onFinishFailed(err);
    }
  };

  const ctxValue = useMemo(() => store, [store]);

  return h(
    FormStoreContext.Provider,
    { value: ctxValue },
    h(
      'form',
      {
        onSubmit: handleSubmit,
        autoComplete,
        className: `${layout === 'horizontal' ? 'flex flex-wrap gap-4' : ''} ${className}`,
        ...rest,
      },
      children,
    ),
  );
};

Form.useForm = useForm;

// ========== Form.Item ==========
Form.Item = function FormItem(props) {
  const {
    label,
    name,
    rules,
    extra,
    noStyle,
    shouldUpdate,
    valuePropName,
    trigger,
    getValueFromEvent,
    children,
    className = '',
  } = props;

  const store = useContext(FormStoreContext);
  const [, forceUpdate] = useState(0);

  // 注册规则
  useEffect(() => {
    if (store && name && rules) store.setFieldRules(name, rules);
  }, [store, name, rules]);

  // 订阅 store 变化 → 重渲染
  useEffect(() => {
    if (!store) return;
    return store.subscribe(() => forceUpdate((n) => n + 1));
  }, [store]);

  // shouldUpdate / render prop
  if (typeof children === 'function' && shouldUpdate) {
    const renderedChild = children({
      getFieldValue: (fn) => (store ? store.getFieldValue(fn) : undefined),
      getFieldsValue: () => (store ? store.getFieldsValue() : {}),
      getFieldError: (fn) => (store ? store.getFieldError(fn) : []),
    });
    if (noStyle) return h(Fragment, null, renderedChild);
    return h(
      'div',
      { className: `mb-4 ${className}` },
      label && h('label', { className: 'block text-sm font-medium text-dark-300 mb-1.5' }, label),
      renderedChild,
      extra && h('div', { className: 'text-xs text-dark-500 mt-1' }, extra),
    );
  }

  const value = store && name !== undefined ? store.getFieldValue(name) : undefined;
  const fieldErrors = store && name !== undefined ? store.getFieldError(name) : [];
  const hasError = fieldErrors.length > 0;

  // 绑定受控 value/onChange
  let childElement = children;
  if (name !== undefined && React.isValidElement(children)) {
    const vp = valuePropName || 'value';
    const tr = trigger || 'onChange';
    const originalHandler = children.props[tr];
    const childProps = {
      [vp]: value !== undefined ? value : children.props[vp],
      [tr]: (...args) => {
        let newVal;
        if (typeof getValueFromEvent === 'function') {
          newVal = getValueFromEvent(...args);
        } else {
          const first = args[0];
          if (first && typeof first === 'object' && 'target' in first && first.target) {
            const target = first.target;
            // checkbox/radio 使用 checked；其它使用 value
            newVal = target.type === 'checkbox' ? target.checked : target.value;
          } else {
            newVal = first;
          }
        }
        if (store) store.setFieldValue(name, newVal);
        if (typeof originalHandler === 'function') originalHandler(...args);
      },
    };
    childElement = React.cloneElement(children, childProps);
  }

  if (noStyle) {
    return h(
      Fragment,
      null,
      childElement,
      hasError && h('div', { className: 'text-red-400 text-xs mt-1' }, fieldErrors[0]),
    );
  }

  return h(
    'div',
    { className: `mb-4 ${className}` },
    label && h('label', { className: 'block text-sm font-medium text-dark-300 mb-1.5' }, label),
    childElement,
    hasError && h('div', { className: 'text-red-400 text-xs mt-1.5' }, fieldErrors[0]),
    extra && h('div', { className: 'text-xs text-dark-500 mt-1' }, extra),
  );
};

export { Form };
